<template>
  <form id="payment-form">
    <div
      id="payment-request-button"
      class="pt-2"
    />
    <div
      id="payment-element"
      class="pt-2"
    />
    <div
      id="error-message"
      class="caption error--text pt-2"
    />
    <VBtn
      class="w-100 shadow-none mt-2"
      color="brandingPrimary"
      data-test="payButton"
      :class="{'disabled': !isFormValid }"
      dark
      @click="pay"
      @click.stop="$emit('trackGtmEventPayButtonClick')"
    >
      <span class="button">
        {{ $t('checkout.paymentForm.pay') }}
      </span>
    </VBtn>
  </form>
</template>

<script lang="ts">
export default {
  name: 'StripeForm',
};
</script>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { StripeElementPayment } from '@vue-stripe/vue-stripe';
import { loadStripe } from '@stripe/stripe-js';
import { useI18n, useRouteRef } from '../../../composables';

const props = defineProps({
  clientSecret: {
    type: String,
    required: true,
  },
  publishableKey: {
    type: String,
    required: true,
  },
  setupIntentId: {
    type: String,
    required: true,
  },
  stripeAccount: {
    type: String,
    required: true,
  },
  amount: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['onPaymentSuccess', 'trackGtmEventPayButtonClick']);

const { t } = useI18n();
const route = useRouteRef();

const stripeElements = ref(null);
const stripe = ref(null);
const paymentElement = ref(null);
const isFormValid = ref(false);
const elementsOptions = {
  appearance: {}, // appearance options
};

const getLocale = computed(() => route.value.params.lang);

function subscribeOnChangeEventOfPaymentElement() {
  paymentElement.value.on('change', (event) => {
    isFormValid.value = event.complete && !event.empty;
  });
}

function showError(message) {
  const messageContainer = document.querySelector('#error-message');
  messageContainer.textContent = message;
}

function setupAppleGooglePay() {
  const paymentRequest = stripe.value.paymentRequest({
    country: 'LU',
    currency: 'eur',
    total: {
      label: t('checkout.paymentForm.total'),
      amount: Math.round(+props.amount * 100), // specific for currency
    },
    requestPayerName: true,
    requestPayerEmail: true,
  });

  const prButton = stripeElements.value.create('paymentRequestButton', {
    paymentRequest: paymentRequest,
  });

  paymentRequest.canMakePayment().then((result) => {
    if (result) {
      prButton.mount('#payment-request-button');
    } else {
      document.getElementById('payment-request-button').style.display =
        'none';
    }
  });
  paymentRequest.on('paymentmethod', (ev) => {
    // Confirm the PaymentIntent without handling potential next actions (yet).
    stripe.value
      .confirmCardSetup(
        props.clientSecret,
        { payment_method: ev.paymentMethod.id },
        { handleActions: false }
      )
      .then((confirmResult) => {
        if (confirmResult.error) {
          // Report to the browser that the payment failed, prompting it to
          // re-show the payment interface, or show an error message and close
          // the payment interface.
          ev.complete('fail');
          showError(confirmResult.error.message);
        } else {
          // Report to the browser that the confirmation was successful, prompting
          // it to close the browser payment method collection interface.
          ev.complete('success');
          emit('onPaymentSuccess', null);
        }
      });
  });
}

async function pay() {
  const { error } = await stripe.value.confirmSetup({
    elements: stripeElements.value,
    redirect: 'if_required',
  });

  if (error) {
    showError(error.message);
  } else {
    emit('onPaymentSuccess', null);
  }
}

onMounted(async () => {
  stripe.value = await loadStripe(props.publishableKey);
  stripeElements.value = stripe.value.elements({
    clientSecret: props.clientSecret,
    locale: getLocale.value,
  });
  paymentElement.value = stripeElements.value.create('payment', {
    wallets: {
      applePay: 'never',
      googlePay: 'never',
    },
  });
  paymentElement.value.mount('#payment-element');
  subscribeOnChangeEventOfPaymentElement();
  setupAppleGooglePay();
});
</script>
